import React from 'react';
import { useTranslation } from 'react-i18next';

import { FileModel } from '../../../../../../typings/api/skymap/rest/v1/.common';
import { SkyMapAxiosServiceFactory } from '../../../../services/axios/skymap-axios-service-factory';
import { publish, SubscriptionTopic } from '../../../../viewer/messaging/pubsub';
import { Button } from '../../button/button';
import { useFileVersions as useFileVersions } from '../hooks/use-file-versions';

interface Props {
  file: FileModel;
  disabled: boolean;
}

/**
 * Button for downloading the latest version of the specified file.
 * During request for fetching S3 URL, button gets disabled with spinning loader icon.
 *
 * If an error occurs when fetching S3 URL, toast is displayed.
 * Any errors when redirecting to S3 URL (to download file) is not handled.
 * For instance, if the URL for some reason is not valid, or if the internet connection is lost,
 * then current page will display the response (S3 XML response for instance, if signed
 * URL is not valid, or any other error in AWS).
 */
const FileDownloadButton = (props: Props) => {
  const { t } = useTranslation();
  const [pending, setPending] = React.useState(false);
  const { lastVersion } = useFileVersions(props.file.versions);

  const onClick = async () => {
    try {
      setPending(true);
      const result = await SkyMapAxiosServiceFactory.instance
        .createFileVersionServiceV1()
        .getFileVersionUrl({
          path: {
            fileVersionId: lastVersion.id,
          },
          query: {},
        });

      window.location.assign(result.data.url);
    } catch (err) {
      publish(SubscriptionTopic.ToastrMessage, {
        message: t('fileManager.actionBar.download.unexpectedError', { ns: 'components' }),
        type: 'error',
      });
    } finally {
      setPending(false);
    }
  };

  return (
    <Button
      disabled={props.disabled || pending}
      leftIcon={{ icon: ['fad', pending ? 'spinner' : 'download'], spin: pending }}
      variant="text"
      onClick={onClick}
    >
      {t('fileManager.actionBar.download.text', { ns: 'components' })}
    </Button>
  );
};

export { FileDownloadButton };
